import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useRef, useState } from "react";
import { List, X, ShieldCheck } from "@phosphor-icons/react";
import { useConsumerAuth } from "../context/ConsumerAuthContext";
import PrivacyControls from "./PrivacyControls";
import InstallApp, { useAppInstall } from "./InstallApp";

const NavItems = ({ onClick, onInstall, showInstall }) => (
  <>
    {[
      { to: "/competitions", label: "Competitions" },
      { to: "/best-odds-for-budget", label: "Budget odds" },
      { to: "/best-value", label: "Best Value" },
      { to: "/operators", label: "Operators" },
      { to: "/guides", label: "Guides" },
      { to: "/about", label: "About" },
    ].map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        onClick={onClick}
        data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
        className={({ isActive }) =>
          `text-sm tracking-wide font-medium transition-colors ${
            isActive ? "text-[var(--cc-gold)]" : "text-white/80 hover:text-white"
          }`
        }
      >
        {l.label}
      </NavLink>
    ))}
    {showInstall && (
      <button
        type="button"
        onClick={(event) => { onInstall(event); onClick?.(); }}
        aria-haspopup="dialog"
        data-testid="nav-install"
        className="text-sm tracking-wide font-medium text-white/80 hover:text-white text-left"
      >
        Install
      </button>
    )}
  </>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const install = useAppInstall();
  const installTrigger = useRef(null);
  const brandLink = useRef(null);
  const openInstall = (event) => {
    installTrigger.current = event.currentTarget;
    setInstallOpen(true);
  };
  const restoreInstallFocus = (event) => {
    event.preventDefault();
    const target = installTrigger.current?.isConnected ? installTrigger.current : brandLink.current;
    target?.focus();
  };
  const loc = useLocation();
  const consumer = useConsumerAuth();
  const consumerLink = consumer?.enabled && consumer?.account ? "/account" : "/signin";
  const consumerLabel = consumer?.enabled && consumer?.account ? "My Account" : "Sign In";

  return (
    <div className="min-h-screen flex flex-col bg-[var(--cc-navy)]">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link ref={brandLink} to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[var(--cc-gold)] text-[var(--cc-navy)]">
              <ShieldCheck weight="fill" size={20} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-display text-lg font-semibold tracking-tight">
                Competition Checker<span className="text-[var(--cc-gold)]">.</span>
              </span>
              <span
                className="hidden sm:inline-block text-[9px] uppercase tracking-[0.18em] text-white/40 font-medium"
                data-testid="brand-tagline"
              >
                Powered by QUOD-Tech
              </span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <NavItems onInstall={openInstall} showInstall={!install.installed} />
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link
              to={consumerLink}
              data-testid="header-signin-link"
              className="text-xs overline text-white/60 hover:text-white"
            >
              {consumerLabel}
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-white/10 bg-[var(--cc-navy)]/95 backdrop-blur">
            <div className="px-6 py-4 flex flex-col gap-4">
              <NavItems onClick={() => setOpen(false)} onInstall={openInstall} showInstall={!install.installed} />
              <Link
                to={consumerLink}
                onClick={() => setOpen(false)}
                className="text-xs overline text-white/60 mt-2"
                data-testid="mobile-signin-link"
              >
                {consumerLabel}
              </Link>
            </div>
          </div>
        )}
      </header>
      <InstallApp open={installOpen} onOpenChange={setInstallOpen} onCloseAutoFocus={restoreInstallFocus} install={install} />

      <main className="flex-1" key={loc.pathname}>
        <Outlet />
      </main>

      <footer className="border-t border-white/10 mt-24 bg-[var(--cc-navy)]">
        <div className="max-w-7xl mx-auto px-6 py-12 grid gap-8 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[var(--cc-gold)] text-[var(--cc-navy)]">
                <ShieldCheck weight="fill" size={20} />
              </span>
              <span className="font-display text-lg font-semibold">Competition Checker</span>
            </div>
            <p className="text-sm text-[var(--cc-text-muted)] leading-relaxed">
              Independent UK prize competition transparency and odds comparison.
              Compare before you enter.
            </p>
          </div>
          <div>
            <div className="overline text-white/40 mb-3">Browse</div>
            <ul className="space-y-2 text-sm">
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/competitions">All competitions</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/best-odds-for-budget">Best odds for your budget</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/best-value">Best value</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/operators">Operators</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/guides">Guides</Link></li>
              <li><button type="button" aria-haspopup="dialog" className="text-white/70 hover:text-[var(--cc-gold)]" onClick={openInstall}>Install help</button></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/how-we-score">How scoring works</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-white/40 mb-3">Trust and legal</div>
            <ul className="space-y-2 text-sm">
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/about">About</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/contact">Contact</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/delete-account">Delete account</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/cookie-policy">Cookie Policy</Link></li>
              <li><PrivacyControls /></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/terms">Terms</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/affiliate-disclosure">Affiliate Disclosure</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/data-accuracy">Data accuracy</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/editorial-methodology">Editorial methodology</Link></li>
              <li><Link className="text-white/70 hover:text-[var(--cc-gold)]" to="/responsible-participation">Responsible Participation</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-white/40 mb-3">Responsible participation</div>
            <p className="text-xs text-[var(--cc-text-muted)] leading-relaxed">
              Only spend what you can comfortably afford. Most prize competitions
              offer a free entry route — check the operator's terms.
            </p>
            <Link
              className="inline-flex mt-3 text-xs text-[var(--cc-gold)] hover:underline"
              to="/responsible-participation"
            >
              Read responsible participation guidance
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 text-xs text-[var(--cc-text-muted)] leading-relaxed space-y-2">
            <section
              className="border-l-2 border-[var(--cc-gold)] pl-4 pb-2"
              data-testid="footer-independent-service"
            >
              <h2 className="overline text-[var(--cc-gold)] mb-2">
                Independent information service
              </h2>
              <p className="text-sm text-white/80" data-testid="footer-disclaimer">
                Competition Checker is an independent UK information and comparison
                service. We do not operate prize competitions, sell tickets, accept
                stakes, process entries or award prizes. We analyse publicly available
                competition information to help UK consumers compare odds, value,
                transparency and operator practices before choosing whether to visit an
                operator's website. Users should always review the operator's official
                terms and free-entry route.
              </p>
            </section>
            <p>
              Competition Checker is operated by QUOD-Tech Ltd. For general
              enquiries, contact{" "}
              <a className="text-white/70 hover:text-[var(--cc-gold)]" href="mailto:info@competitionchecker.com">
                info@competitionchecker.com
              </a>
              . For business or partnership enquiries, contact{" "}
              <a className="text-white/70 hover:text-[var(--cc-gold)]" href="mailto:info@quod-tech.com">
                info@quod-tech.com
              </a>
              .
            </p>
            <p>
              Competition Checker may earn commission from some outbound links
              where an operator has an approved affiliate or partner
              arrangement with us. Affiliate links are clearly labelled where
              used. Affiliate relationships do not influence scoring, ranking,
              odds calculations, operator trust notes, or whether a competition
              is shown.
            </p>
            <p className="pt-2 text-white/30">© {new Date().getFullYear()} Competition Checker.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
